import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import type { BudgetSummary, ClientProfile, IngestReport, Me, ProgramDetail, ProgramLine, ProgramList, Tag } from '../lib/api';
import { fmtDate, fmtDateTime, titleCase } from '../lib/format';
import { fmtK, fmtPct, pct, STAGE_LABEL, STAGE_ORDER } from '../lib/money';
import { Empty, Kpi, Modal, PageHead, Panel, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { DecisionButtons, DecisionPill, ScoreBadge } from '../components/matches';

const EXHIBITS = [{ id: 'all', label: 'R-1 + P-1' }, { id: 'r1', label: 'R-1 RDT&E' }, { id: 'p1', label: 'P-1 Procurement' }];
const SERVICES = ['Army', 'Navy', 'Marine Corps', 'Air Force', 'Space Force', 'Defense-Wide'];
const SORTS = [
  { id: 'request', label: 'Largest request' }, { id: 'delta', label: 'Biggest increase' }, { id: 'cut', label: 'Biggest cut' },
  { id: 'delta_pct', label: 'Fastest growth' }, { id: 'mark', label: 'Marked first' }, { id: 'title', label: 'Title A to Z' }, { id: 'fit', label: 'Best fit', needsClient: true },
];
const PAGE = 100;

export function Budget({ params, me }: { params: string[]; me: Me }) {
  const routeId = params[0] === 'id' ? (params[1] ?? null) : null;
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [fy, setFy] = useState<number | null>(null);
  const [exhibit, setExhibit] = useState('all');
  const [service, setService] = useState('');
  const [tag, setTag] = useState('');
  const [client, setClient] = useState('');
  const [flags, setFlags] = useState<{ new_starts: boolean; marked: boolean; fit_only: boolean }>({ new_starts: false, marked: false, fit_only: false });
  const [sort, setSort] = useState('request');
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ProgramList | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(routeId);
  const [err, setErr] = useState<string | null>(null);
  const seq = useRef(0);

  const loadSummary = useCallback((year?: number | null) => {
    api<BudgetSummary>(`/api/budget/summary${year ? `?fy=${year}` : ''}`).then((s) => { setSummary(s); if (!fy) setFy(s.budget_year); }).catch((e: Error) => setErr(e.message));
  }, [fy]);
  useEffect(() => {
    loadSummary(fy);
    api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([]));
    api<{ items: ClientProfile[] }>('/api/clients').then((r) => setClients(r.items)).catch(() => setClients([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSelected(routeId); }, [routeId]);

  const change = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setOffset(0); };
  const chooseClient = (id: string) => { setClient(id); setSort(id ? 'fit' : sort === 'fit' ? 'request' : sort); setFlags((f) => ({ ...f, fit_only: !!id })); setOffset(0); };

  useEffect(() => {
    if (!fy) return;
    const p = new URLSearchParams({ fy: String(fy), limit: String(PAGE), offset: String(offset), sort });
    if (exhibit !== 'all') p.set('exhibit', exhibit);
    if (service) p.set('service', service);
    if (tag) p.set('tag', tag);
    if (q) p.set('q', q);
    if (client) p.set('client', client);
    if (flags.new_starts) p.set('new_starts', '1');
    if (flags.marked) p.set('marked', '1');
    if (client && flags.fit_only) p.set('fit_only', '1');
    const mine = ++seq.current;
    setLoading(true);
    api<ProgramList>(`/api/budget/programs?${p.toString()}`)
      .then((r) => { if (mine !== seq.current) return; setData((prev) => (offset > 0 && prev ? { ...r, items: [...prev.items, ...r.items] } : r)); })
      .catch((e: Error) => { if (mine === seq.current) setErr(e.message); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [fy, exhibit, service, tag, q, client, flags, sort, offset]);

  const items = data?.items ?? [];
  const s = data?.summary;
  const by = data?.budget_year ?? fy ?? 0, cy = data?.current_year ?? by - 1, py = data?.prior_year ?? by - 2;
  const canEdit = me.role !== 'viewer';
  const closeDetail = useCallback(() => { setSelected(null); if (routeId) location.hash = '/budget'; }, [routeId]);
  const refresh = () => { setOffset(0); setData(null); loadSummary(fy); seq.current++; setTimeout(() => setOffset((o) => o), 0); };
  const noDocs = summary && summary.documents.length === 0;

  return (
    <>
      <PageHead
        title="Budget & Appropriations"
        sub="Program elements and procurement lines from the President's Budget exhibits, every observed figure kept, congressional marks recorded as they land"
        actions={
          <>
            <form className="search" onSubmit={(e: FormEvent) => { e.preventDefault(); change(setQ)(draft.trim()); }} style={{ minWidth: 240 }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="PE, line item, title, account…" />
            </form>
            <label className="sort-control"><span className="muted sm">Budget year</span>
              <select className="select" value={fy ?? ''} onChange={(e) => { const v = Number(e.target.value); setFy(v); setOffset(0); loadSummary(v); }}>
                {(summary?.years ?? (fy ? [fy] : [])).map((y) => <option key={y} value={y}>FY{y}</option>)}
              </select>
            </label>
            <label className="sort-control"><span className="muted sm">Fit for</span>
              <select className="select" value={client} onChange={(e) => chooseClient(e.target.value)}>
                <option value="">No client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="sort-control"><span className="muted sm">Sort</span>
              <select className="select" value={sort} onChange={(e) => change(setSort)(e.target.value)}>
                {SORTS.filter((x) => !x.needsClient || client).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </label>
          </>
        }
      />
      {err && <div className="error-text" style={{ marginBottom: 10 }}>{err}</div>}

      <div className="kpi-row">
        <Kpi label="Budget lines" value={s ? s.total.toLocaleString() : '…'} hint={exhibit === 'all' ? 'R-1 + P-1' : exhibit.toUpperCase()} />
        <Kpi label={`FY${by} request`} value={s ? fmtK(s.by_total) : '…'} hint={s && s.by_request !== s.by_total ? `discretionary ${fmtK(s.by_request)}` : undefined} />
        <Kpi label={`vs FY${cy} enacted`} value={s ? fmtPct(pct(s.cy_enacted, s.by_total)) || '—' : '…'} hint={s ? `${fmtK(s.cy_enacted)} → ${fmtK(s.by_total)}` : undefined} />
        <Kpi label={`FY${py} actual`} value={s ? fmtK(s.py_actual) : '…'} />
        <Kpi label="New starts" value={s ? s.new_starts : '…'} hint="funded in BY, nothing before" />
        <Kpi label="Congressional marks" value={s ? s.marked : '…'} hint={summary ? `${summary.matched_lines} lines matched to clients` : undefined} />
      </div>

      <div className="filter-bar">
        {EXHIBITS.map((x) => <button key={x.id} className={`filter-btn ${exhibit === x.id ? 'active' : ''}`} onClick={() => change(setExhibit)(x.id)}>{x.label}</button>)}
        <span className="filter-gap" />
        <button className={`filter-btn ${service === '' ? 'active' : ''}`} onClick={() => change(setService)('')}>All services</button>
        {SERVICES.map((x) => <button key={x} className={`filter-btn ${service === x ? 'active' : ''}`} onClick={() => change(setService)(x)}>{x}</button>)}
        <span className="filter-gap" />
        <button className={`filter-btn ${flags.new_starts ? 'active' : ''}`} onClick={() => { setFlags({ ...flags, new_starts: !flags.new_starts }); setOffset(0); }}>New starts</button>
        <button className={`filter-btn ${flags.marked ? 'active' : ''}`} onClick={() => { setFlags({ ...flags, marked: !flags.marked }); setOffset(0); }}>Marked</button>
        {client && <button className={`filter-btn ${flags.fit_only ? 'active' : ''}`} onClick={() => { setFlags({ ...flags, fit_only: !flags.fit_only }); setOffset(0); }}>Fit only</button>}
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${tag === '' ? 'active' : ''}`} onClick={() => change(setTag)('')}>Any technology</button>
        {tags.map((t) => <button key={t.id} className={`filter-btn ${tag === t.id ? 'active' : ''}`} onClick={() => change(setTag)(t.id)}>{t.label}</button>)}
      </div>

      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-head">
          <h2>{data ? `${items.length.toLocaleString()} of ${s!.total.toLocaleString()} lines` : 'Loading…'}{client && clients.find((c) => c.id === client) ? ` · fit for ${clients.find((c) => c.id === client)!.name}` : ''}</h2>
          <span className="muted sm">$ thousands in the exhibits, shown in millions</span>
        </div>
        <div className="panel-body table-wrap">
          {noDocs ? (
            <Empty>No budget exhibits loaded yet.{canEdit ? ' Use "Load exhibits" below to pull the R-1 and P-1 for a budget year from the Comptroller.' : ''}</Empty>
          ) : loading && !data ? <Empty>Loading…</Empty> : items.length === 0 ? <Empty>Nothing matches these filters.</Empty> : (
            <table className="data-table budget-table">
              <thead>
                <tr>
                  {client && <th>Fit</th>}
                  <th>Line</th><th>Program</th><th>Service</th>
                  <th className="num">FY{py} actual</th><th className="num">FY{cy} enacted</th><th className="num">FY{by} request</th><th className="num">Δ vs FY{cy}</th><th>Mark</th>
                </tr>
              </thead>
              <tbody>
                {items.map((g) => <Row key={g.id} g={g} tags={tags} showFit={!!client} canDecide={canEdit} onOpen={() => setSelected(g.id)} />)}
              </tbody>
            </table>
          )}
        </div>
        {data && items.length < s!.total && (
          <div className="panel-body" style={{ borderTop: '1px solid var(--line-2)', textAlign: 'center' }}>
            <button className="ghost-btn" disabled={loading} onClick={() => setOffset(items.length)}>{loading ? 'Loading…' : `Load ${Math.min(PAGE, s!.total - items.length)} more`}</button>
          </div>
        )}
      </div>

      {summary && summary.documents.length > 0 && (
        <div className="grid grid-2">
          <Panel title={`Biggest increases, FY${by} vs FY${cy}`}>
            <MoverTable rows={summary.increases} onOpen={setSelected} />
          </Panel>
          <Panel title={`Biggest cuts, FY${by} vs FY${cy}`}>
            <MoverTable rows={summary.decreases} onOpen={setSelected} />
          </Panel>
        </div>
      )}

      <div className="grid grid-2">
        <Panel title="Exhibits loaded" action={summary ? <span className="muted sm">{summary.documents.length} document{summary.documents.length === 1 ? '' : 's'}</span> : undefined}>
          {summary && summary.documents.length > 0 && (
            <table className="data-table" style={{ marginBottom: 14 }}>
              <thead><tr><th>Document</th><th className="num">Lines</th><th className="num">New figures</th><th>Totals</th><th>Loaded</th></tr></thead>
              <tbody>
                {summary.documents.map((d) => {
                  const amounts = (d.totals ?? []).filter((t) => t.kind === 'amount' && t.sheet_total !== null);
                  const ok = amounts.length > 0 && amounts.every((t) => t.matches);
                  return (
                    <tr key={d.id}>
                      <td><strong>{d.title}</strong><div className="muted sm">{d.source_url ? <a className="link" href={d.source_url} target="_blank" rel="noreferrer">{d.filename}</a> : d.filename}</div></td>
                      <td className="num">{d.lines_created.toLocaleString()}</td>
                      <td className="num">{d.versions_new.toLocaleString()}</td>
                      <td>{amounts.length ? <Pill kind={ok ? 'ok' : 'warn'}>{ok ? `${amounts.length} columns reconcile` : 'check totals'}</Pill> : <span className="muted">—</span>}</td>
                      <td className="muted">{fmtDate(d.uploaded_at)}{d.uploaded_by ? ` · ${d.uploaded_by}` : ' · scheduled'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {canEdit ? <LoadExhibits onLoaded={refresh} defaultYear={(summary?.years[0] ?? new Date().getUTCFullYear()) + 1} /> : <div className="muted sm">Owners and analysts can load exhibits.</div>}
        </Panel>

        <Panel title="What changed" action={<span className="muted sm">re-observed figures, last 60 days</span>}>
          {!summary ? <Empty>Loading…</Empty> : summary.changes.length === 0 ? (
            <Empty>No figure has changed since it was first loaded. Changes appear here when a revised exhibit, a congressional mark or a reprogramming moves a line.</Empty>
          ) : summary.changes.map((c) => {
            const d = pct(c.prev, c.amount_thousands);
            return (
              <div className="alert" key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(c.program_id)}>
                <span className={`alert-tag ${Math.abs(c.amount_thousands ?? 0) - Math.abs(c.prev ?? 0) >= 0 ? 'notable' : 'material'}`}>FY{c.fiscal_year} {STAGE_LABEL[c.stage] ?? c.stage}</span>
                <div className="alert-body">
                  <div><strong>{c.pe_number ?? c.line_item}</strong> {c.title}</div>
                  <div className="muted sm">{fmtK(c.prev)} → {fmtK(c.amount_thousands)} ({fmtPct(d)}) · {c.service} · {fmtDateTime(c.observed_at)}{c.source_id === 'manual' ? ' · recorded by analyst' : ''}</div>
                </div>
              </div>
            );
          })}
        </Panel>
      </div>

      {selected && <ProgramModal id={selected} me={me} tags={tags} budgetYear={by} onClose={closeDetail} onChanged={() => loadSummary(fy)} />}
    </>
  );
}

function Delta({ from, to }: { from: number | null; to: number | null }) {
  if (from === null || to === null) return <span className="muted">{from === null && to !== null ? 'new' : to === null && from !== null ? 'ends' : '—'}</span>;
  const d = to - from;
  const p = pct(from, to);
  return <span className={d > 0 ? 'pos' : d < 0 ? 'neg' : 'muted'}>{fmtK(d, { sign: true })}<div className="sm muted">{fmtPct(p)}</div></span>;
}

function Row({ g, tags, showFit, canDecide, onOpen }: { g: ProgramLine; tags: Tag[]; showFit: boolean; canDecide: boolean; onOpen: () => void }) {
  return (
    <tr className="clickable" onClick={onOpen}>
      {showFit && (
        <td onClick={(e) => e.stopPropagation()}>
          {g.match_id ? <><ScoreBadge score={g.fit} />{canDecide ? <div style={{ marginTop: 6 }}><DecisionButtons matchId={g.match_id} decision={g.fit_decision ?? 'pending'} onChanged={() => undefined} /></div> : g.fit_decision && g.fit_decision !== 'pending' && <div style={{ marginTop: 6 }}><DecisionPill decision={g.fit_decision} /></div>}</> : <span className="muted sm">—</span>}
        </td>
      )}
      <td className="mono">{g.pe_number ?? g.line_item}<div className="muted sm">{g.exhibit === 'r1' ? 'R-1' : 'P-1'} · {g.account_code}</div></td>
      <td><strong>{g.title}</strong><div className="muted sm">{g.ba_title ?? g.appropriation}</div>{g.tags.length > 0 && <div style={{ marginTop: 4 }}><TagChips tags={g.tags} all={tags} max={3} /></div>}</td>
      <td className="muted">{g.service}</td>
      <td className="num">{fmtK(g.py_actual)}</td>
      <td className="num">{fmtK(g.cy_enacted)}</td>
      <td className="num"><strong>{fmtK(g.by_total)}</strong>{g.by_request !== null && g.by_total !== null && g.by_request !== g.by_total && <div className="muted sm">disc {fmtK(g.by_request)}</div>}</td>
      <td className="num"><Delta from={g.cy_enacted} to={g.by_total} /></td>
      <td>{g.mark ? <Pill kind="warn">{STAGE_LABEL[g.mark.stage]} {fmtK(g.mark.amount)}</Pill> : <span className="muted">—</span>}</td>
    </tr>
  );
}

function MoverTable({ rows, onOpen }: { rows: ProgramLine[]; onOpen: (id: string) => void }) {
  if (!rows.length) return <Empty>Needs two budget years loaded to compare.</Empty>;
  return (
    <table className="data-table">
      <tbody>
        {rows.map((g) => (
          <tr key={g.id} className="clickable" onClick={() => onOpen(g.id)}>
            <td><strong>{g.title}</strong><div className="muted sm">{g.pe_number ?? g.line_item} · {g.service}</div></td>
            <td className="num">{fmtK(g.cy_enacted)} → <strong>{fmtK(g.by_total)}</strong></td>
            <td className="num"><Delta from={g.cy_enacted} to={g.by_total} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LoadExhibits({ onLoaded, defaultYear }: { onLoaded: () => void; defaultYear: number }) {
  const [year, setYear] = useState(String(defaultYear));
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const report = (r: IngestReport, matching?: { upserted: number } | null) => setResult({
    ok: r.totals_ok || r.already_loaded,
    text: r.already_loaded
      ? `${r.filename} was already loaded (document ${r.document_id}); nothing changed.`
      : `${r.filename}: ${r.lines.toLocaleString()} lines, ${r.programs_created} new programs, ${r.versions_new.toLocaleString()} new figures, ${r.versions_same.toLocaleString()} unchanged, ${r.tags} tags. Totals ${r.totals_ok ? 'reconcile with the sheet' : 'DO NOT reconcile, check the document'}.${matching ? ` ${matching.upserted} client matches scored.` : ''}${r.warnings.length ? ` Warnings: ${r.warnings.slice(0, 2).join('; ')}` : ''}`,
  });

  async function fetchExhibit(exhibit: 'r1' | 'p1') {
    setBusy(exhibit); setResult(null);
    try {
      const r = await api<{ report: IngestReport; matching: { upserted: number } | null }>('/api/budget/documents/fetch', { method: 'POST', json: { fiscal_year: Number(year), exhibit } });
      report(r.report, r.matching); onLoaded();
    } catch (e) { setResult({ ok: false, text: e instanceof ApiError && e.status === 502 ? `The Comptroller did not return a workbook for FY${year} ${exhibit.toUpperCase()}. It may not be published yet.` : (e as Error).message }); }
    finally { setBusy(null); }
  }
  async function upload(file: File) {
    setBusy('upload'); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch('/api/budget/documents', { method: 'POST', body: fd, credentials: 'same-origin' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail ?? j.error ?? res.statusText);
      report(j.report, j.matching); onLoaded();
    } catch (e) { setResult({ ok: false, text: (e as Error).message }); }
    finally { setBusy(null); }
  }

  return (
    <div>
      <div className="muted sm" style={{ marginBottom: 8 }}>Load exhibits: pull straight from comptroller.war.gov, or upload a workbook you downloaded. Loading the same file twice is harmless.</div>
      <div className="form-grid" style={{ alignItems: 'end' }}>
        <div className="field"><label htmlFor="bx-year">Budget year</label><input id="bx-year" className="input" type="number" min={2015} max={2100} value={year} onChange={(e) => setYear(e.target.value)} /></div>
        <div className="field"><label>From the Comptroller</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary-btn" disabled={!!busy} onClick={() => fetchExhibit('r1')}>{busy === 'r1' ? 'Loading…' : `Fetch FY${year} R-1`}</button>
            <button className="primary-btn" disabled={!!busy} onClick={() => fetchExhibit('p1')}>{busy === 'p1' ? 'Loading…' : `Fetch FY${year} P-1`}</button>
          </div>
        </div>
        <div className="field span-2"><label htmlFor="bx-file">Or upload an R-1 / P-1 display workbook (.xlsx)</label>
          <input id="bx-file" className="input" type="file" accept=".xlsx" disabled={!!busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </div>
      </div>
      {busy === 'upload' && <div className="muted sm" style={{ marginTop: 8 }}>Uploading and parsing…</div>}
      {result && <div className={result.ok ? 'reveal' : 'error-text'} style={{ marginTop: 10 }}>{result.text}</div>}
    </div>
  );
}

function ProgramModal({ id, me, tags, budgetYear, onClose, onChanged }: { id: string; me: Me; tags: Tag[]; budgetYear: number; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<ProgramDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mark, setMark] = useState({ fiscal_year: String(budgetYear), stage: 'hac', amount: '', source_document: '', source_locator: '', notes: '' });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => { api<ProgramDetail>(`/api/budget/programs/${id}`).then(setD).catch((e: Error) => setErr(e.message)); }, [id]);
  useEffect(load, [load]);
  const canEdit = me.role !== 'viewer';

  async function recordMark(e: FormEvent) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const amount = mark.amount.trim() === '' ? null : Math.round(Number(mark.amount.replace(/[,$\s]/g, '')));
      await api(`/api/budget/programs/${id}/versions`, { method: 'POST', json: { fiscal_year: Number(mark.fiscal_year), stage: mark.stage, amount_thousands: amount, source_document: mark.source_document.trim(), source_locator: mark.source_locator.trim() || undefined, notes: mark.notes.trim() || undefined } });
      setMark({ ...mark, amount: '', source_locator: '', notes: '' });
      load(); onChanged();
    } catch (ex) { setErr((ex as Error).message); } finally { setSaving(false); }
  }
  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try { await api(`/api/budget/programs/${id}/notes`, { method: 'POST', json: { body: note.trim() } }); setNote(''); load(); }
    catch (ex) { setErr((ex as Error).message); } finally { setSaving(false); }
  }

  // Latest value per (FY, stage) for the grid; the full observation list sits underneath.
  const latest = new Map<string, ProgramDetail['versions'][number]>();
  for (const v of d?.versions ?? []) latest.set(`${v.fiscal_year}:${v.stage}`, v);
  const years = [...new Set((d?.versions ?? []).map((v) => v.fiscal_year))].sort();
  const stages = STAGE_ORDER.filter((s) => (d?.versions ?? []).some((v) => v.stage === s));

  return (
    <Modal title={d ? `${d.pe_number ?? d.line_item} · ${d.title}` : 'Loading…'} onClose={onClose}>
      {err && <div className="error-text">{err}</div>}
      {d && (
        <>
          <div className="filter-bar" style={{ marginBottom: 12 }}>
            <Pill kind="info">{d.pe_number ? 'R-1 RDT&E' : 'P-1 Procurement'}</Pill>
            {d.service && <Pill kind="muted">{d.service}</Pill>}
            <TagChips tags={d.tags} all={tags} max={6} showConfidence />
          </div>
          <dl className="kv">
            <dt>Account</dt><dd>{d.account_code} · {d.appropriation}</dd>
            <dt>Budget activity</dt><dd>{d.budget_activity ? `${d.budget_activity} · ` : ''}{d.ba_title ?? '—'}</dd>
            <dt>Organization</dt><dd>{d.organization ?? '—'}</dd>
          </dl>

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Funding timeline <span className="muted sm">($ thousands, latest observation per cell)</span></h3>
          {years.length === 0 ? <div className="muted sm">No figures yet.</div> : (
            <div className="table-wrap">
              <table className="data-table timeline">
                <thead><tr><th>FY</th>{stages.map((s) => <th key={s} className="num">{STAGE_LABEL[s]}</th>)}</tr></thead>
                <tbody>
                  {years.map((y) => (
                    <tr key={y}>
                      <td><strong>FY{y}</strong></td>
                      {stages.map((s) => { const v = latest.get(`${y}:${s}`); return <td key={s} className={`num ${['hac', 'sac', 'hasc', 'sasc', 'conference'].includes(s) ? 'mark-cell' : ''}`} title={v ? `${fmtDateTime(v.observed_at)} · ${v.document_title ?? v.source_document ?? v.source_id}` : ''}>{v ? (v.amount_thousands === null ? '—' : v.amount_thousands.toLocaleString()) : ''}</td>; })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Observations ({d.versions.length}) <button className="link" style={{ background: 'none', border: 0, cursor: 'pointer' }} onClick={() => setShowAll(!showAll)}>{showAll ? 'hide' : 'show'}</button></h3>
          {showAll && [...d.versions].sort((a, b) => b.observed_at.localeCompare(a.observed_at)).map((v) => (
            <div className="version" key={v.id}>
              <time>{fmtDateTime(v.observed_at)}</time>
              <span>FY{v.fiscal_year} {STAGE_LABEL[v.stage] ?? v.stage}: <strong>{v.amount_thousands === null ? '—' : `$${v.amount_thousands.toLocaleString()}K`}</strong>{v.quantity !== null ? ` · qty ${v.quantity}` : ''} <span className="muted sm">· {v.document_title ?? v.source_document ?? v.source_id}{v.recorded_by ? ` · ${v.recorded_by}` : ''}{v.source_locator ? ` · ${v.source_locator}` : ''}</span></span>
            </div>
          ))}

          {canEdit && (
            <form onSubmit={recordMark} style={{ marginTop: 14 }}>
              <h3 style={{ fontSize: 13, marginBottom: 6 }}>Record a congressional mark or adjustment</h3>
              <div className="form-grid">
                <div className="field"><label htmlFor="mk-fy">Fiscal year</label><input id="mk-fy" className="input" type="number" min={2000} max={2100} required value={mark.fiscal_year} onChange={(e) => setMark({ ...mark, fiscal_year: e.target.value })} /></div>
                <div className="field"><label htmlFor="mk-stage">Stage</label>
                  <select id="mk-stage" className="select" value={mark.stage} onChange={(e) => setMark({ ...mark, stage: e.target.value })}>
                    {['hac', 'sac', 'hasc', 'sasc', 'conference', 'enacted', 'reprogramming', 'other'].map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                  </select>
                </div>
                <div className="field"><label htmlFor="mk-amt">Amount ($ thousands, blank = zeroed)</label><input id="mk-amt" className="input" inputMode="numeric" value={mark.amount} onChange={(e) => setMark({ ...mark, amount: e.target.value })} placeholder="e.g. 95000" /></div>
                <div className="field"><label htmlFor="mk-src">Source document</label><input id="mk-src" className="input" required minLength={3} value={mark.source_document} onChange={(e) => setMark({ ...mark, source_document: e.target.value })} placeholder="H. Rept. 119-xxx, Division C" /></div>
                <div className="field"><label htmlFor="mk-loc">Page / table</label><input id="mk-loc" className="input" value={mark.source_locator} onChange={(e) => setMark({ ...mark, source_locator: e.target.value })} placeholder="p. 214" /></div>
                <div className="field"><label htmlFor="mk-notes">Language / rationale</label><input id="mk-notes" className="input" value={mark.notes} onChange={(e) => setMark({ ...mark, notes: e.target.value })} placeholder="Program increase: prototype acceleration" /></div>
              </div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={saving || !mark.source_document.trim()}>{saving ? 'Saving…' : 'Record'}</button></div>
            </form>
          )}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Relevance by client ({d.matches.length})</h3>
          {d.matches.length === 0 && <div className="muted sm">No client profile scores against this line.</div>}
          {d.matches.map((m) => (
            <div className="version" key={m.id} style={{ alignItems: 'flex-start' }}>
              <time><ScoreBadge score={m.score} /></time>
              <span style={{ flex: 1 }}>
                <a className="link" href={`#/clients/${m.client_id}`}>{m.client_name}</a>
                <span className="muted sm rationale">
                  {[...(m.rationale.tags ?? []).map((t) => `${tags.find((x) => x.id === t.tag_id)?.label ?? t.tag_id} +${t.points}`), ...(m.rationale.capabilities ?? []).map((c) => `"${c.phrase}" +${c.points}`), m.rationale.agency?.matched ? `${titleCase(m.rationale.agency.matched.toLowerCase())} +${m.rationale.agency.points}` : null].filter(Boolean).join(' · ')}
                </span>
              </span>
              {canEdit ? <DecisionButtons matchId={m.id} decision={m.decision} onChanged={load} /> : m.decision !== 'pending' && <DecisionPill decision={m.decision} />}
            </div>
          ))}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Notes ({d.notes.length})</h3>
          {d.notes.map((n) => (
            <div className="note" key={n.id}><div><span className="tag new" style={{ marginLeft: 0, marginRight: 8 }}>{titleCase(n.kind).toUpperCase()}</span>{n.body}</div><div className="meta">{n.author ?? 'Unknown'} · {fmtDateTime(n.created_at)}</div></div>
          ))}
          {canEdit && (
            <form onSubmit={addNote} style={{ marginTop: 10 }}>
              <div className="field"><label htmlFor="pg-note">Add a note</label><textarea id="pg-note" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Program office contacts, transition pathway, why this line matters…" /></div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={saving || !note.trim()}>{saving ? 'Saving…' : 'Save note'}</button></div>
            </form>
          )}
        </>
      )}
    </Modal>
  );
}
